import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Paper,
  TextareaAutosize,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  TextField,
  Grid,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import { ensureHttps, logUrl } from '../utils/urlUtils';
import BuildIcon from '@mui/icons-material/Build';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ padding: '24px 0' }}>
      {value === index && children}
    </div>
  );
}

export default function WorkDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { jobs, updateJob, teams } = useData();
  const [activeTab, setActiveTab] = useState(0);
  const [newNote, setNewNote] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  let work, parentJob;
  for (const job of jobs) {
    const found = (job.workItems || []).find(w => w.id === id);
    if (found) {
      work = found;
      parentJob = job;
      break;
    }
  }

  if (!work) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5">Work not found</Typography>
        <Button
          variant="contained"
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
        >
          Back
        </Button>
      </Box>
    );
  }

  // Helper to update the work item in parent job
  const updateWorkInJob = (updatedWork) => {
    const updatedWorkItems = (parentJob.workItems || []).map(w => w.id === updatedWork.id ? updatedWork : w);
    updateJob(parentJob.id, { ...parentJob, workItems: updatedWorkItems });
  };

  const handleAddNote = () => {
    if (!newNote.trim()) return;

    const updatedWorkItems = parentJob.workItems.map(item => {
      if (item.id === work.id) {
        const currentNotes = item.notes || [];
        return {
          ...item,
          notes: [...currentNotes, {
            text: newNote,
            date: new Date().toISOString(),
            createdBy: 'Admin'
          }]
        };
      }
      return item;
    });

    updateJob(parentJob.id, {
      ...parentJob,
      workItems: updatedWorkItems
    });

    setNewNote('');
  };

  const handleDeleteNote = (noteIndex) => {
    const updatedWorkItems = parentJob.workItems.map(item => {
      if (item.id === work.id) {
        const currentNotes = item.notes || [];
        return {
          ...item,
          notes: currentNotes.filter((_, index) => index !== noteIndex)
        };
      }
      return item;
    });

    updateJob(parentJob.id, {
      ...parentJob,
      workItems: updatedWorkItems
    });
  };

  const handleTeamDialogOpen = () => {
    setTeamDialogOpen(true);
  };

  const handleTeamDialogClose = () => {
    setTeamDialogOpen(false);
  };

  const handleAssignTeam = () => {
    if (selectedTeam) {
      const updatedWork = { ...work, team: selectedTeam };
      updateWorkInJob(updatedWork);
      handleTeamDialogClose();
    }
  };

  // --- Attachment Upload ---
  const handleAttachmentUpload = async (e) => {
    setUploadError('');
    setUploading(true);
    const file = e.target.files[0];
    if (!file) {
      setUploading(false);
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const attachments = work.attachments || [];
      const newAttachment = {
        id: Date.now().toString(),
        name: file.name,
        type: file.type.startsWith('image/') ? 'image' : 'file',
        url: event.target.result
      };
      const updatedWork = {
        ...work,
        attachments: [...attachments, newAttachment],
        updatedAt: new Date().toISOString()
      };
      updateWorkInJob(updatedWork);
      setUploading(false);
    };
    reader.onerror = () => {
      setUploadError('Failed to read file.');
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  // --- Remove Attachment ---
  const handleRemoveAttachment = (attachmentId) => {
    const updatedWork = {
      ...work,
      attachments: (work.attachments || []).filter(att => att.id !== attachmentId),
      updatedAt: new Date().toISOString()
    };
    updateWorkInJob(updatedWork);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <BuildIcon sx={{ mr: 1 }} />
            <Typography variant="h4">
              {work.name}
            </Typography>
          </Box>
        </Box>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {work.description}
        </Typography>
        {work.team && (
          <Typography variant="subtitle1" sx={{ mt: 1 }}>
            Assigned Team: {work.team}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          Created on {new Date(work.createdAt).toLocaleDateString()} at {new Date(work.createdAt).toLocaleTimeString()}
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label={`Notes (${work.notes?.length || 0})`} />
          <Tab label={`Attachments (${work.attachments?.length || 0})`} />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        <Box sx={{ mb: 3 }}>
          {(work.notes || []).map((note, index) => (
            <Paper key={index} sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {note.text}
                </Typography>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDeleteNote(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
              <Typography variant="body2" color="text.secondary">
                By {note.createdBy} on {new Date(note.date).toLocaleDateString()} at {new Date(note.date).toLocaleTimeString()}
              </Typography>
            </Paper>
          ))}
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Add New Note
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Type your note here..."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            onClick={handleAddNote}
            disabled={!newNote.trim()}
            startIcon={<AddIcon />}
          >
            Add Note
          </Button>
        </Box>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            component="label"
            disabled={uploading}
          >
            Upload Attachment
            <input
              type="file"
              hidden
              onChange={handleAttachmentUpload}
              accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/zip,application/x-zip-compressed,application/octet-stream,.doc,.docx,.xls,.xlsx,.pdf,.zip,.png,.jpg,.jpeg,.gif"
            />
          </Button>
          {uploading && <Typography sx={{ ml: 2 }} color="text.secondary">Uploading...</Typography>}
          {uploadError && <Typography color="error">{uploadError}</Typography>}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {(work.attachments || []).length === 0 && (
            <Typography color="text.secondary">No attachments uploaded.</Typography>
          )}
          {(work.attachments || []).map((att) => {
            const httpsUrl = ensureHttps(att.url);
            logUrl(`Work Detail Attachment ${att.id}`, httpsUrl);
            return (
              <Paper key={att.id} sx={{ p: 2, minWidth: 180, maxWidth: 220, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                {att.type === 'image' ? (
                  <img 
                    src={httpsUrl} 
                    alt={att.name} 
                    onLoad={() => logUrl(`Work Detail Attachment Image Load ${att.id}`, httpsUrl)}
                    onError={(e) => {
                      console.error('Error loading attachment image:', httpsUrl);
                      logUrl(`Work Detail Attachment Image Error ${att.id}`, httpsUrl);
                    }}
                    style={{ width: '100%', maxHeight: 120, objectFit: 'cover', marginBottom: 8 }} 
                  />
                ) : (
                  <Box sx={{ width: '100%', textAlign: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">{att.name}</Typography>
                  </Box>
                )}
                <Typography variant="caption" sx={{ wordBreak: 'break-all', mb: 1 }}>{att.name}</Typography>
                <Button size="small" color="error" onClick={() => handleRemoveAttachment(att.id)}>
                  Remove
                </Button>
                <Button size="small" href={httpsUrl} target="_blank" sx={{ mt: 1 }}>
                  View
                </Button>
              </Paper>
            );
          })}
        </Box>
      </TabPanel>

      <Paper sx={{ mt: 3 }}>
        <List>
          <ListItem>
            <ListItemText 
              primary="Assign Team" 
              secondary="Assign a team to this work item"
            />
            <Button variant="outlined" size="small" onClick={handleTeamDialogOpen}>
              Assign Team
            </Button>
          </ListItem>
        </List>
      </Paper>

      <Dialog open={teamDialogOpen} onClose={handleTeamDialogClose}>
        <DialogTitle>Assign Team</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Team</InputLabel>
            <Select
              value={selectedTeam}
              onChange={(e) => setSelectedTeam(e.target.value)}
              label="Team"
            >
              {teams && teams.map((team) => (
                <MenuItem key={team.id} value={team.name}>
                  {team.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTeamDialogClose}>Cancel</Button>
          <Button onClick={handleAssignTeam} variant="contained" color="primary">
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 